import { getBatchByBatchId } from 'features/autocheck/autoCheckApi'
import { BatchProcessingStatus } from 'features/autocheck/types'
import ProgressBarComponent from 'features/autocheck/utils/ProgressBar'
import { useEffect, useRef, useState } from 'react'
import { FaInfoCircle } from 'react-icons/fa'
import { Button, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'

import { AllClaimFailedMessage } from '../internal/AllClaimFailedMessage'
import { CLAIM_FILE_VALIDATION_CHECK, CLAIM_VALIDATE_TIMER, FILE_VALIDATE_PROCESS } from './Constants'

interface IProps {
  activeStep: (val: number) => void
  batchId?: string
  clearValidateTimeOut: (value: any) => void
  startOver: () => void
}

export const ClaimFileValidate = ({ activeStep, batchId, clearValidateTimeOut, startOver }: IProps) => {
  const [validateProcess, setValidateProcess] = useState(FILE_VALIDATE_PROCESS.count_1)
  const [btnEnable, setBtnEnable] = useState(false)
  const [isValidationFailed, setIsValidationFailed] = useState(false)
  const isValidated = useRef(false)
  const validateTimer = useRef(CLAIM_VALIDATE_TIMER.TWO_HALF_SEC)
  const isInitialized = useRef(false)
  const clearTimerRef = useRef<any>(null)

  let validateCount = 0

  useEffect(() => {
    if (isInitialized.current === false) {
      isInitialized.current = true
      getValidateProcess(1)
    }
  }, [])

  useEffect(() => {
    if (!batchId || isValidated.current) {
      return
    }
    checkIsClaimValidated(batchId)
  }, [isValidated.current])

  function checkIsClaimValidated(batchId: string, revalidateTo?: number) {
    getBatchByBatchId(batchId)
      .then((batch) => {
        const processingStatus = batch?.data[0]?.processingStatus

        if (
          processingStatus === BatchProcessingStatus.Initiallizing ||
          processingStatus === BatchProcessingStatus.Queued ||
          processingStatus === BatchProcessingStatus.Validating
        ) {
          isValidated.current = false
          setIsValidationFailed(false)
        } else if (processingStatus === BatchProcessingStatus.FailedToComplete) {
          isValidated.current = false
          setIsValidationFailed(true)
          activeStep(1)
          return
        } else {
          isValidated.current = true
        }

        if (revalidateTo) {
          const count = isValidated.current === true ? 4 : revalidateTo
          getValidateProcess(count)
        }
      })
      .catch(() => {
        isValidated.current = false
        setIsValidationFailed(true)
        activeStep(1)
        return
      })
  }

  function getValidateProcess(step: number | undefined) {
    clearTimerRef.current = setTimeout(() => {
      switch (step) {
        case 1:
          setValidateProcess(FILE_VALIDATE_PROCESS.count_1)

          if (isValidated.current) {
            getValidateProcess(4)
          } else if (validateCount === 0 && batchId) {
            validateTimer.current = CLAIM_VALIDATE_TIMER.TWO_HALF_SEC
            checkIsClaimValidated(batchId, 1)
            validateCount++
          } else {
            validateCount = 0
            getValidateProcess(2)
          }
          break
        case 2:
          setValidateProcess(FILE_VALIDATE_PROCESS.count_2)
          validateTimer.current = CLAIM_VALIDATE_TIMER.FIVE_SEC
          if (isValidated.current) {
            getValidateProcess(4)
          } else if (validateCount === 0 && batchId) {
            checkIsClaimValidated(batchId, 2)
            validateCount++
          } else {
            getValidateProcess(3)
          }
          break
        case 3:
          setValidateProcess(FILE_VALIDATE_PROCESS.count_3)
          validateTimer.current = CLAIM_VALIDATE_TIMER.TEN_SEC
          if (isValidated.current) {
            getValidateProcess(4)
          } else {
            if (batchId) {
              checkIsClaimValidated(batchId, 3)
            }
            return
          }
          break
        case 4:
          validateCount = 0
          setValidateProcess(FILE_VALIDATE_PROCESS.count_4)
          setBtnEnable(true)
          break
        case 5:
          setBtnEnable(true)
          return
      }
    }, validateTimer.current)
    clearValidateTimeOut(clearTimerRef.current)
  }

  return isValidationFailed ? (
    <AllClaimFailedMessage startOver={startOver} />
  ) : (
    <>
      {' '}
      <Div>
        <h2>{validateProcess.title}</h2>
        <div className="wrapper">
          <div className="progress-container">
            <ProgressBarComponent percent={validateProcess.progress} />
          </div>
          <div className="progress-content-container">
            {CLAIM_FILE_VALIDATION_CHECK.map((value, i) => (
              <div className="progress-content-inner" key={i}>
                {validateProcess.icon} <span>{value.title}</span>
                <Tooltip
                  key={i}
                  className="text-with-tooltip"
                  tooltipText={value.tooltip}
                  position={PositionEnum.right}
                  positionContent={100}
                  positionTooltipArrow={30}
                  tooltipStyles={{
                    'max-width': '19rem',
                    width: '19rem',
                    transform: 'translateY(-20%) translateX(11%)',
                    zIndex: 101,
                  }}
                  hideTimeout={0}
                >
                  <FaInfoCircle className="info-icon" />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      </Div>
      <ButtonContainer>
        <Button
          data-testid="file-validate-btn"
          variant="info"
          radius="rounded"
          className="btn-validate"
          size="large"
          color="info"
          disabled={!btnEnable}
          onClick={() => activeStep(2)}
        >
          Continue
        </Button>
      </ButtonContainer>
    </>
  )
}

const Div = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem;
  padding: 1.5rem;
  margin-top: 0;
  border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  border-radius: 8px;
  .wrapper {
    width: 15.9375rem;
  }
  h2 {
    text-align: center;
  }
  .progress-container {
    margin: 0 auto;
  }
  .progress-content-container {
    display: flex;
    flex-direction: column;
    padding-top: 1.25rem;
    .progress-content-inner {
      margin: 0.5rem 0;
      display: flex;
      .loader-icon {
        margin-right: 0.625rem;
        color: ${({ theme }) => theme.colors.grey.light};
      }
      .info-icon {
        margin-left: 0.625rem;
        color: ${({ theme }) => theme.colors.grey.light};
      }
      span {
        color: ${({ theme }) => theme.colors.neutral.neutral};
        font-size: 1rem;
        font-weight: 600;
      }
      .tooltip-container {
        span {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.33;
          letter-spacing: normal;
          text-align: left;
          color: ${({ theme }) => theme.colors.neutral.neutral};
        }
      }
    }
  }
`

const ButtonContainer = styled.div`
  margin: 2rem;
  button {
    width: 100%;
    height: 3rem;
  }
`
